import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24 bg-gradient-to-b from-silver-bright to-white" data-testid="contact-page">
      {/* Hero Banner */}
      <section className="page-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1703565426315-4209c2e88eea?w=1920&q=80"
            alt="Contact Us"
            className="w-full h-full object-cover"
          />
          <div className="page-hero-overlay" />
        </div>
        <div className="page-hero-content">
          <div className="max-w-3xl">
            <p className="text-malva-light font-sans text-sm tracking-[0.3em] uppercase mb-4">
              Get in Touch
            </p>
            <h1 className="font-serif text-4xl md:text-6xl text-white mb-6">
              Contact Us
            </h1>
            <p className="text-white/80 text-lg">
              We'd love to hear from you. Whether you have a question about reservations, 
              events, or anything else, our team is ready to assist.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding">
        <div className="container-elegant">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <div className="card-elegant p-8 md:p-10">
              {success ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-serif text-2xl text-gray-800 mb-4">Message Sent!</h3>
                  <p className="text-gray-600 mb-2">
                    Thank you for reaching out to us.
                  </p>
                  <p className="text-gray-500 mb-6 text-sm">
                    We've received your message and will get back to you within <strong>48 hours</strong>. 
                    A confirmation has been sent to your email.
                  </p>
                  <div className="bg-malva/10 rounded-lg p-4 mb-6 text-left">
                    <p className="text-sm text-gray-600">
                      <strong>Your Reference:</strong> #{Date.now().toString(36).toUpperCase()}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Please keep this reference number for your records.
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                    }}
                    className="btn-secondary"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-700">Full Name *</Label>
                      <Input id="name" name="name" required value={formData.name} onChange={handleChange}
                        placeholder="John Smith" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="contact-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700">Phone</Label>
                      <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange}
                        placeholder="+44 20 1234 5678" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="contact-phone" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">Email *</Label>
                    <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange}
                      placeholder="john@example.com" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="contact-email" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-gray-700">Subject *</Label>
                    <Input id="subject" name="subject" required value={formData.subject} onChange={handleChange}
                      placeholder="How can we help?" className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400" data-testid="contact-subject" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-700">Message *</Label>
                    <Textarea id="message" name="message" required value={formData.message} onChange={handleChange}
                      placeholder="Tell us more about your inquiry..." rows={5}
                      className="bg-silver-bright/50 border-malva/20 text-gray-800 placeholder:text-gray-400 resize-none" data-testid="contact-message" />
                  </div>

                  <Button type="submit" disabled={loading} className="w-full btn-primary h-14" data-testid="contact-submit">
                    {loading ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="card-elegant p-6 bg-gradient-to-br from-white to-malva/5">
                <h3 className="font-serif text-2xl text-gray-800 mb-6">Visit Us</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-malva/20 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-malva-dark" />
                    </div>
                    <div>
                      <p className="text-gray-700 font-serif mb-1">Address</p>
                      <p className="text-gray-500">45-47 York St<br />Twickenham, TW1 3LP</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-malva/20 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-malva-dark" />
                    </div>
                    <div>
                      <p className="text-gray-700 font-serif mb-1">Phone</p>
                      <a href="tel:+442088929841" className="text-gray-500 hover:text-malva-dark transition-colors">
                        +44 20 8892 9841
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-malva/20 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-malva-dark" />
                    </div>
                    <div>
                      <p className="text-gray-700 font-serif mb-1">Email</p>
                      <a href="mailto:bookings@revadining.co.uk" className="text-gray-500 hover:text-malva-dark transition-colors">
                        bookings@revadining.co.uk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-malva/20 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-malva-dark" />
                    </div>
                    <div>
                      <p className="text-gray-700 font-serif mb-1">Hours</p>
                      <div className="text-gray-500 text-sm space-y-1">
                        <div><span className="font-medium">Lunch:</span> Mon - Fri: 12:00 PM - 03:00 PM</div>
                        <div><span className="font-medium">Dinner:</span> Mon - Fri: 05:00 PM - 11:00 PM</div>
                        <div className="mt-2"><span className="font-medium">Lunch:</span> Sat - Sun: 12:00 PM - 03:00 PM</div>
                        <div><span className="font-medium">Dinner:</span> Sat - Sun: 05:00 PM - 11:00 PM</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-elegant overflow-hidden">
                <div className="aspect-video">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2488.5876!2d-0.3298!3d51.4492!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760e7e5b4c7c1f%3A0x1234567890abcdef!2s45-47%20York%20St%2C%20Twickenham%20TW1%203LP%2C%20UK!5e0!3m2!1sen!2suk!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Reva Dining Location"
                    className="w-full h-full"
                    data-testid="google-map"
                  />
                </div>
              </div>

              <div className="card-elegant p-6 bg-gradient-to-br from-malva/10 to-white">
                <p className="text-gray-700 font-serif text-lg mb-2">Private Events</p>
                <p className="text-gray-500 text-sm mb-4">
                  Planning a special celebration? Our private dining room is perfect for 
                  intimate gatherings and corporate events.
                </p>
                <a href="mailto:bookings@revadining.co.uk" className="text-malva-dark hover:text-malva-darker transition-colors duration-300 text-sm font-medium">
                  bookings@revadining.co.uk
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
